import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Minus, 
  Trash2, 
  Check,
  AlertCircle,
  Sparkles,
  Tag,
  Send,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import MobileLayout from '../layouts/MobileLayout';
import { Service, serviceApiService } from '../../services/serviceApi';
import { Pack, packsApiService } from '../../services/packsApi';
import { promotionApiService } from '../../services/promotionApi';

interface DevisItem {
  service?: Service;
  pack?: Pack;
  type: 'service' | 'pack';
  quantity: number;
  pages?: number;
  description?: string;
}

const MobileDevisRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [devisItems, setDevisItems] = useState<DevisItem[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [allPacks, setAllPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const [showPackSelector, setShowPackSelector] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  
  // Codes promo
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [savedPromoCode, setSavedPromoCode] = useState<string | null>(null);
  const [showPromoSuggestion, setShowPromoSuggestion] = useState(false);

  // Info client
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });

  useEffect(() => {
    loadData();
    checkSavedPromoCodes();
  }, [location.state]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [services, packs] = await Promise.all([
        serviceApiService.getServices(),
        packsApiService.getPacks(true)
      ]);
      
      setAllServices(services);
      setAllPacks(packs);
      
      // Pré-sélection si fournie
      const selectedService = location.state?.selectedService as Service;
      const selectedPack = location.state?.selectedPack as Pack;
      
      if (selectedService) {
        setDevisItems([{
          service: selectedService,
          type: 'service',
          quantity: 1,
          pages: selectedService.unit === 'page' ? 8 : undefined,
          description: ''
        }]);
      } else if (selectedPack) {
        setDevisItems([{
          pack: selectedPack,
          type: 'pack',
          quantity: 1,
          description: ''
        }]);
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSavedPromoCodes = async () => {
    try {
      const userPromoCodes = await promotionApiService.getUserPromoCodes();
      if (userPromoCodes.length > 0) {
        const validCodes = userPromoCodes.filter((promo: any) => {
          if (promo.end_date) {
            const endDate = new Date(promo.end_date);
            return endDate > new Date() && promo.status === 'active';
          }
          return promo.status === 'active';
        });

        if (validCodes.length > 0) {
          const bestCode = validCodes.reduce((best: any, current: any) => 
            current.discount_value > best.discount_value ? current : best
          );
          setSavedPromoCode(bestCode.code);
          setShowPromoSuggestion(true);
        }
      }
    } catch (error) {
      console.error('Erreur codes promo:', error);
    }
  };

  const addService = (service: Service) => {
    const exists = devisItems.find(item => item.type === 'service' && item.service?.id === service.id);
    if (!exists) {
      setDevisItems([...devisItems, {
        service,
        type: 'service',
        quantity: 1,
        pages: service.unit === 'page' ? 8 : undefined,
        description: ''
      }]);
      setShowServiceSelector(false);
    }
  };

  const addPack = (pack: Pack) => {
    const exists = devisItems.find(item => item.type === 'pack' && item.pack?.id === pack.id);
    if (!exists) {
      setDevisItems([...devisItems, {
        pack,
        type: 'pack',
        quantity: 1,
        description: ''
      }]);
      setShowPackSelector(false);
    }
  };

  const updateQuantity = (itemId: string, itemType: 'service' | 'pack', delta: number) => {
    setDevisItems(items => 
      items.map(item => {
        if ((itemType === 'service' && item.type === 'service' && item.service?.id === itemId) ||
            (itemType === 'pack' && item.type === 'pack' && item.pack?.id === itemId)) {
          const newQty = Math.max(1, item.quantity + delta);
          return { ...item, quantity: newQty };
        }
        return item;
      })
    );
  };

  const updatePages = (serviceId: string, delta: number) => {
    setDevisItems(items => 
      items.map(item => 
        item.type === 'service' && item.service?.id === serviceId 
          ? { ...item, pages: Math.max(1, (item.pages || 1) + delta) }
          : item
      )
    );
  };

  const removeItem = (itemId: string, itemType: 'service' | 'pack') => {
    setDevisItems(items => 
      items.filter(item => {
        if (itemType === 'service' && item.type === 'service') {
          return item.service?.id !== itemId;
        }
        if (itemType === 'pack' && item.type === 'pack') {
          return item.pack?.id !== itemId;
        }
        return true;
      })
    );
  };

  const calculateItemPrice = (item: DevisItem): number => {
    if (item.type === 'service' && item.service) {
      let basePrice = item.service.price || 0;
      if (item.service.unit === 'page' && item.pages) {
        basePrice *= item.pages;
      }
      return basePrice * item.quantity;
    }
    if (item.type === 'pack' && item.pack) {
      return item.pack.total_price * item.quantity;
    }
    return 0;
  };

  const calculateTotal = (): number => {
    return devisItems.reduce((total, item) => total + calculateItemPrice(item), 0);
  };

  const checkPromoCode = async () => {
    try {
      setPromoError('');
      const response = await promotionApiService.validatePromoCode(promoCode);
      
      if (response.valid && response.promotion) {
        const promotion = response.promotion;
        let discount = 0;
        
        if (promotion.discount_type === 'percentage') {
          discount = Number(promotion.discount_value) || 0;
        } else if (promotion.discount_type === 'fixed_amount') {
          const subtotal = calculateTotal();
          const fixedAmount = Number(promotion.discount_value) || 0;
          if (subtotal > 0 && fixedAmount > 0) {
            discount = (fixedAmount / subtotal) * 100;
          }
        }
        
        setPromoDiscount(Number(discount) || 0);
        return discount;
      } else {
        setPromoError(response.error || 'Code invalide');
        setPromoDiscount(0);
        return 0;
      }
    } catch (error) {
      setPromoError('Erreur vérification');
      setPromoDiscount(0);
      return 0;
    }
  };

  const useSavedPromoCode = async () => {
    if (savedPromoCode) {
      setPromoCode(savedPromoCode);
      setShowPromoSuggestion(false);
      
      // Appliquer directement le code promo
      try {
        setPromoError('');
        const response = await promotionApiService.validatePromoCode(savedPromoCode);
        
        if (response.valid && response.promotion) {
          const promotion = response.promotion;
          let discount = 0;
          
          if (promotion.discount_type === 'percentage') {
            discount = Number(promotion.discount_value) || 0;
          } else if (promotion.discount_type === 'fixed_amount') {
            const subtotal = calculateTotal();
            const fixedAmount = Number(promotion.discount_value) || 0;
            if (subtotal > 0 && fixedAmount > 0) {
              discount = (fixedAmount / subtotal) * 100;
            }
          }
          
          setPromoDiscount(Number(discount) || 0);
        } else {
          setPromoError(response.error || 'Code invalide');
          setPromoDiscount(0);
        }
      } catch (error) {
        console.error('Erreur lors de l\'application du code promo:', error);
        setPromoError('Erreur lors de l\'application');
        setPromoDiscount(0);
      }
    }
  };

  const calculateTotalWithDiscount = () => {
    const subtotal = calculateTotal();
    const discount = (subtotal * promoDiscount) / 100;
    return {
      subtotal,
      discount,
      total: subtotal - discount
    };
  };

  const handleSubmit = async () => {
    let finalPromoDiscount = promoDiscount;
    if (promoCode && promoDiscount === 0) {
      finalPromoDiscount = await checkPromoCode();
    }

    navigate('/facture-proforma', { 
      state: { 
        selectedServices: devisItems,
        promoDiscount: finalPromoDiscount,
        promoCode: promoCode
      } 
    });
  };

  const isCalculable = devisItems.length > 0 && devisItems.every(item => {
    if (item.type === 'service' && item.service) {
      return item.service.price && item.service.price > 0 && item.service.price_type !== 'from';
    }
    if (item.type === 'pack' && item.pack) {
      const hasVariablePriceServices = item.pack.services?.some(service => service.price_type === 'from');
      return item.pack.total_price && item.pack.total_price > 0 && !hasVariablePriceServices;
    }
    return false;
  });

  const hasFromPrices = devisItems.some(item => 
    (item.type === 'service' && item.service?.price_type === 'from') ||
    (item.type === 'pack' && item.pack?.services?.some(service => service.price_type === 'from'))
  );

  const toggleItemExpand = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const totals = calculateTotalWithDiscount();

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/90 hover:text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Retour</span>
        </button>
        <h1 className="text-2xl font-bold text-white mb-2">Demande de Devis</h1>
        <p className="text-sm text-white/90">
          Personnalisez votre solution
        </p>
      </div>

      {/* Contenu */}
      <div className="px-4 py-6 space-y-4">
        {/* Items sélectionnés */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Ma sélection</h2>
            <span className="text-sm text-gray-500">{devisItems.length} élément{devisItems.length > 1 ? 's' : ''}</span>
          </div>

          {devisItems.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertCircle className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">Aucun service sélectionné</p>
            </div>
          ) : (
            <div className="space-y-3">
              {devisItems.map((item) => {
                const itemId = item.type === 'service' ? item.service!.id : item.pack!.id;
                const itemName = item.type === 'service' ? item.service!.name : item.pack!.name;
                const itemPrice = calculateItemPrice(item);
                const isExpanded = expandedItems.has(itemId);

                return (
                  <div key={itemId} className="border border-gray-200 rounded-xl p-3">
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm">{itemName}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {item.type === 'service' ? 'Service' : 'Pack'}
                          {item.type === 'service' && item.service?.price_type === 'from' && ' (Prix sur devis)'}
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(itemId, item.type)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Quantité */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-600">Quantité</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(itemId, item.type, -1)}
                          className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center"
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center font-semibold text-sm text-gray-900">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(itemId, item.type, 1)}
                          className="w-7 h-7 bg-amber-500 text-white rounded-lg flex items-center justify-center"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Pages (si service) */}
                    {item.type === 'service' && item.service?.unit === 'page' && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600">Nb. pages</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updatePages(itemId, -1)}
                            className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center"
                            disabled={(item.pages || 1) <= 1}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center font-semibold text-sm text-gray-900">{item.pages || 1}</span>
                          <button
                            onClick={() => updatePages(itemId, 1)}
                            className="w-7 h-7 bg-amber-500 text-white rounded-lg flex items-center justify-center"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Prix */}
                    {item.type === 'service' && item.service?.price_type !== 'from' && (
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-600">Total</span>
                        <span className="font-bold text-amber-600">{itemPrice.toLocaleString()} FCFA</span>
                      </div>
                    )}
                    {item.type === 'pack' && (
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-600">Total</span>
                        <span className="font-bold text-amber-600">{itemPrice.toLocaleString()} FCFA</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Boutons d'ajout */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              onClick={() => setShowServiceSelector(!showServiceSelector)}
              className="py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Service
            </button>
            <button
              onClick={() => setShowPackSelector(!showPackSelector)}
              className="py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Pack
            </button>
          </div>
        </div>

        {/* Sélecteurs */}
        {showServiceSelector && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-3">Sélectionner un service</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {allServices.map((service) => (
                <button
                  key={service.id}
                  onClick={() => addService(service)}
                  className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <div className="font-semibold text-sm text-gray-900">{service.name}</div>
                  <div className="text-xs text-gray-500 mt-1">{service.price_display}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {showPackSelector && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-3">Sélectionner un pack</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {allPacks.map((pack) => (
                <button
                  key={pack.id}
                  onClick={() => addPack(pack)}
                  className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <div className="font-semibold text-sm text-gray-900">{pack.name}</div>
                  <div className="text-xs text-gray-500 mt-1">{pack.total_price.toLocaleString()} FCFA</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Suggestion code promo */}
        {showPromoSuggestion && savedPromoCode && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-green-900 mb-1">Code promo disponible !</h3>
                <p className="text-sm text-green-700 mb-3">
                  Utilisez <strong>{savedPromoCode}</strong> pour bénéficier de votre réduction
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={useSavedPromoCode}
                    className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium"
                  >
                    Utiliser
                  </button>
                  <button
                    onClick={() => setShowPromoSuggestion(false)}
                    className="px-4 py-2 bg-white text-green-600 border border-green-200 rounded-lg text-sm"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Code promo */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Tag className="w-5 h-5 text-amber-600" />
            Code promo
          </h3>
          <input
            type="text"
            value={promoCode}
            onChange={(e) => {
              const code = e.target.value.toUpperCase();
              setPromoCode(code);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && promoCode.length > 3) {
                checkPromoCode();
              }
            }}
            onBlur={() => {
              if (promoCode.length > 3) {
                checkPromoCode();
              }
            }}
            placeholder="Entrez votre code et validez"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-500"
          />
          {promoError && (
            <p className="text-xs text-red-600 mt-2">{promoError}</p>
          )}
          {promoDiscount > 0 && (
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              <Check className="w-3 h-3" />
              Code valide : {promoDiscount}% de réduction
            </p>
          )}
        </div>

        {/* Total */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-200">
          <h3 className="font-bold text-gray-900 mb-3">Récapitulatif</h3>
          {isCalculable ? (
            <>
              <div className="space-y-2 mb-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total</span>
                  <span className="font-semibold">{totals.subtotal.toLocaleString()} FCFA</span>
                </div>
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Réduction ({promoDiscount}%)</span>
                    <span className="font-semibold">-{totals.discount.toLocaleString()} FCFA</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-amber-200">
                <span className="font-bold text-lg text-gray-900">Total</span>
                <span className="font-bold text-2xl text-amber-600">{totals.total.toLocaleString()} FCFA</span>
              </div>
            </>
          ) : (
            <div className="text-center py-2">
              <p className="text-sm text-gray-600">
                {hasFromPrices 
                  ? "Prix sur devis - Un expert vous contactera"
                  : "Sélectionnez des services pour voir le total"
                }
              </p>
            </div>
          )}
        </div>

        {/* Bouton de soumission */}
        <button
          onClick={handleSubmit}
          disabled={devisItems.length === 0}
          className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-bold text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Send className="w-5 h-5" />
          Continuer vers la facture
        </button>
      </div>
    </MobileLayout>
  );
};

export default MobileDevisRequestPage;
